import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyAdminSession, fetchSiteSettings, updateSiteSetting } from '../services/api';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';

const SETTINGS_SCHEMA = [
  {
    group: 'Identité du site',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
    ),
    fields: [
      { key: 'site_name',    label: 'Nom du site',  type: 'text',     placeholder: 'Pixel3000' },
      { key: 'site_tagline', label: 'Tagline',       type: 'text',     placeholder: 'Portfolio & CV en ligne' },
    ],
  },
  {
    group: 'Footer',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
    ),
    fields: [
      { key: 'footer_copyright', label: 'Copyright',        type: 'text',     placeholder: '© 2026 Pixel3000. Tous droits réservés.' },
      { key: 'footer_tagline',   label: 'Tagline footer',   type: 'text',     placeholder: 'Partagez vos documents en toute sécurité.' },
    ],
  },
  {
    group: 'Contacts & Liens',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    ),
    fields: [
      { key: 'contact_email',    label: 'Email de contact', type: 'email',    placeholder: 'contact@example.com' },
      { key: 'contact_linkedin', label: 'URL LinkedIn',     type: 'url',      placeholder: 'https://linkedin.com/in/...' },
      { key: 'contact_github',   label: 'URL GitHub',       type: 'url',      placeholder: 'https://github.com/...' },
    ],
  },
  {
    group: 'Viewer PDF',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    ),
    fields: [
      { key: 'viewer_default_zoom',   label: 'Zoom par défaut (%)',  type: 'number', placeholder: '180', min: 50, max: 300 },
      { key: 'viewer_allow_download', label: 'Téléchargement autorisé', type: 'select', options: [{ value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }] },
    ],
  },
  {
    group: 'Paramètres techniques',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    ),
    fields: [
      { key: 'jwt_expiration_hours',  label: 'Durée des tokens (heures)', type: 'number', placeholder: '48', min: 1, max: 720 },
      { key: 'rate_limit_per_minute', label: 'Rate limit (req/min)',       type: 'number', placeholder: '10', min: 1, max: 300 },
      { key: 'watermark_enabled',     label: 'Filigrane activé',          type: 'select', options: [{ value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }] },
      { key: 'analytics_enabled',     label: 'Analytics activé',          type: 'select', options: [{ value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }] },
    ],
  },
];

export default function SiteSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    verifyAdminSession().then(valid => {
      if (!valid) navigate('/admin/login');
    });
  }, [navigate]);

  useEffect(() => {
    fetchSiteSettings()
      .then(data => setSettings(data))
      .catch(() => showToast('Erreur lors du chargement des paramètres', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (key, value) => {
    setSaving(key);
    try {
      await updateSiteSetting(key, value);
      setSettings(prev => ({ ...prev, [key]: value }));
      showToast('Paramètre sauvegardé');
    } catch {
      showToast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Paramètres du site</h1>
            <p className="text-gray-600">Configurez les informations affichées sur le site et dans le footer.</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2.5 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-300 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Retour</span>
          </button>
        </div>

        {/* Groupes de paramètres */}
        <div className="space-y-6">
          {SETTINGS_SCHEMA.map(group => (
            <div key={group.group} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-3">
                <div className="p-2 bg-primary-50 rounded-lg">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {group.icon}
                  </svg>
                </div>
                <h2 className="font-semibold text-gray-800">{group.group}</h2>
              </div>

              <div className="divide-y divide-gray-50">
                {group.fields.map(field => (
                  <SettingRow
                    key={field.key}
                    field={field}
                    value={settings[field.key] ?? ''}
                    saving={saving === field.key}
                    onSave={handleSave}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Toast toast={toast} onClose={hideToast} />
    </div>
  );
}

function SettingRow({ field, value, saving, onSave }) {
  const [current, setCurrent] = useState(value);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setCurrent(value);
    setDirty(false);
  }, [value]);

  const handleChange = (val) => {
    setCurrent(val);
    setDirty(val !== value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (dirty) onSave(field.key, current);
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
      <label className="w-52 text-sm font-medium text-gray-700 flex-shrink-0">
        {field.label}
      </label>

      <div className="flex-1">
        {field.type === 'select' ? (
          <select
            value={current}
            onChange={e => handleChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
          >
            {field.options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <input
            type={field.type}
            value={current}
            onChange={e => handleChange(e.target.value)}
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        )}
      </div>

      <button
        type="submit"
        disabled={!dirty || saving}
        className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          dirty && !saving
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {saving ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        ) : 'Sauvegarder'}
      </button>
    </form>
  );
}
